import { DEFAULT_ENGAGEMENT_SURVEY_FILTERS } from 'consts/consts';
import React, { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ColorStop, getColor } from 'utils/util/color';
import { stripHtml, sumObjectValues } from '../../../utils/util/util';
import {
  BLUE_DARK,
  BLUE_LIGHT,
  GRAY_DARK,
  GRAY_LIGHT,
  GREEN_DARK,
  GREEN_LIGHT,
  LIKERT_SCALE,
  type RatingScaleItem,
  RED_DARK,
  RED_LIGHT,
  TEXT_COLOR,
  WHITE,
  YELLOW_DARK,
  YELLOW_LIGHT,
} from './chartColors';
import Heatmap, { AdditionalInput } from './Heatmap/Heatmap';
import {
  FormatValueResult,
  GenerateCsvData,
  GetColorFunc,
  GetValueFunc,
  HeatmapCellColorInfo,
} from './Heatmap/types';
import HorizontalBar from './HorizontalBar';

const mergeResponses = (responseList) => {
  return responseList.reduce(
    (accumulator, responses) => {
      accumulator.eligible += responses.eligible ?? 0;
      const keys = Object.keys(responses.responses ?? {});
      keys.forEach((key) => {
        if (!accumulator.responses[key]) {
          accumulator.responses[key] = 0;
        }
        accumulator.responses[key] += responses.responses[key];
      });
      return accumulator;
    },
    {
      eligible: 0,
      responses: {},
    }
  );
};

const _COMPUTE_COLOR = (value, scale: ColorStop[]) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return { background: '', text: '' };
  }
  const textColor = TEXT_COLOR;

  const backgroundColor = getColor(value, scale);

  return {
    background: backgroundColor,
    text: textColor,
  };
};

const COLOR_SCALE = (value) => {
  const scale: ColorStop[] = [
    { value: 1, color: RED_DARK },
    { value: 2, color: RED_LIGHT },
    { value: 3, color: GRAY_LIGHT },
    { value: 4, color: GREEN_LIGHT },
    { value: 5, color: GREEN_DARK },
  ];

  return _COMPUTE_COLOR(value, scale);
};

export const COLOR_SCALE_ALTERNATE = (value) => {
  const scale: ColorStop[] = [
    { value: 1, color: YELLOW_DARK },
    { value: 2, color: YELLOW_LIGHT },
    { value: 3, color: GRAY_LIGHT },
    { value: 4, color: BLUE_LIGHT },
    { value: 5, color: BLUE_DARK },
  ];

  return _COMPUTE_COLOR(value, scale);
};

const COLOR_SCALE_DELTA = (value) => {
  const scale: ColorStop[] = [
    { value: -100, color: RED_DARK },
    { value: -50, color: RED_LIGHT },
    { value: 0, color: GRAY_LIGHT },
    { value: 50, color: GREEN_LIGHT },
    { value: 100, color: GREEN_DARK },
  ];

  return _COMPUTE_COLOR(value, scale);
};

const COLOR_SCALE_DELTA_ALTERNATE = (value) => {
  const scale: ColorStop[] = [
    { value: -100, color: YELLOW_DARK },
    { value: -50, color: YELLOW_LIGHT },
    { value: 0, color: GRAY_LIGHT },
    { value: 50, color: BLUE_LIGHT },
    { value: 100, color: BLUE_DARK },
  ];

  return _COMPUTE_COLOR(value, scale);
};

const percentageColorScale = (
  value,
  color: string,
  _rowIndex,
  _columnIndex,
  _heatmapState
): HeatmapCellColorInfo => {
  const textColor = TEXT_COLOR;
  const scale: ColorStop[] = [
    { value: 0, color: WHITE },
    { value: 100, color: color },
  ];

  if (typeof value !== 'number' || Number.isNaN(value)) {
    return {
      background: scale[0].color,
      text: textColor,
    };
  }

  const backgroundColor = getColor(value, scale);

  return {
    background: backgroundColor,
    text: textColor,
  };
};

export const COLOR_DELTA_FROM_ALL: GetColorFunc = (
  value,
  _data,
  rowIndex,
  _columnIndex,
  heatmapState
) => {
  // @ts-expect-error
  const allValue = heatmapState?.frozenRowData?.[rowIndex]?._value ?? 0;
  const delta =
    Number.isFinite(value) && Number.isFinite(allValue) ? value - allValue : 0;
  return COLOR_SCALE_DELTA(delta);
};

export const COLOR_DELTA_FROM_ALL_ALTERNATE: GetColorFunc = (
  value,
  _data,
  rowIndex,
  _columnIndex,
  heatmapState
) => {
  // @ts-expect-error
  const allValue = heatmapState?.frozenRowData?.[rowIndex]?._value ?? 0;
  const delta =
    Number.isFinite(value) && Number.isFinite(allValue) ? value - allValue : 0;
  return COLOR_SCALE_DELTA_ALTERNATE(delta);
};

export const COLOR_FAVOURABLE: GetColorFunc = (
  value,
  _data,
  rowIndex,
  columnIndex,
  heatmapState
) => {
  return percentageColorScale(
    value,
    GREEN_DARK,
    rowIndex,
    columnIndex,
    heatmapState
  );
};

export const COLOR_FAVOURABLE_ALTERNATE: GetColorFunc = (
  value,
  _data,
  rowIndex,
  columnIndex,
  heatmapState
) => {
  return percentageColorScale(
    value,
    BLUE_DARK,
    rowIndex,
    columnIndex,
    heatmapState
  );
};

export const COLOR_UNFAVORABLE: GetColorFunc = (
  value,
  _data,
  rowIndex,
  columnIndex,
  heatmapState
) => {
  return percentageColorScale(
    value,
    RED_DARK,
    rowIndex,
    columnIndex,
    heatmapState
  );
};

export const COLOR_UNFAVORABLE_ALTERNATE: GetColorFunc = (
  value,
  _data,
  rowIndex,
  columnIndex,
  heatmapState
) => {
  return percentageColorScale(
    value,
    YELLOW_DARK,
    rowIndex,
    columnIndex,
    heatmapState
  );
};

export const COLOR_NEUTRAL: GetColorFunc = (
  value,
  _data,
  rowIndex,
  columnIndex,
  heatmapState
) => {
  return percentageColorScale(
    value,
    GRAY_DARK,
    rowIndex,
    columnIndex,
    heatmapState
  );
};

export const FORMAT_VALUE: GetValueFunc = (valueObj): FormatValueResult => {
  // No responses
  if (!valueObj || Object.keys(valueObj ?? {}).length === 0) {
    return '-';
  }

  let responseCount = 0;
  let responseSum = 0;

  for (const score in valueObj) {
    const numScore = Number(score);
    const numResponses = Number(valueObj[score]);

    responseCount += numResponses;
    responseSum += numScore * numResponses;
  }

  if (responseCount == 0) {
    return '-';
  }
  return Math.round((10 * responseSum) / responseCount) / 10;
};

const computeScalePercentages = (scale, obj) => {
  const ROUNDING_DELTA = 0.009;
  const total = sumObjectValues(obj);
  const result = {
    total: total,
  };

  scale.forEach(
    (v) =>
      (result[v.value] =
        total === 0
          ? total
          : Math.round(((obj[v.value] || 0) / total) * 100) / 100)
  );
  const totalPerc = scale.reduce((acc, v) => acc + (result[v.value] || 0), 0);

  const diff = 1 - totalPerc;
  if (Math.abs(diff) > ROUNDING_DELTA && total > 0) {
    const scaleValues = scale.map((v) => v.value);
    // if scaleValue includes 3, move it at the beginning
    if (scaleValues.includes(3)) {
      const index = scaleValues.indexOf(3);
      scaleValues.splice(index, 1);
      scaleValues.unshift(3);
    }

    // add the missing percentage to the first scale value that has at least one response
    for (const v of scaleValues) {
      if (obj[v] > 0) {
        result[v] += diff;
        break;
      }
    }
  }

  return result;
};

const computePercentage = (valueObj) => {
  const x = computeScalePercentages(LIKERT_SCALE, valueObj);

  const values = {
    favourable: x[5] + x[4],
    neutral: x[3],
    unfavourable: x[2] + x[1],
    total: x.total,
  };

  return values;
};

const formatValuePercentages = (
  valueObj,
  type: 'favourable' | 'neutral' | 'unfavourable'
): FormatValueResult => {
  // No responses
  if (!valueObj || Object.keys(valueObj ?? {}).length === 0) {
    return '-';
  }

  const percentages = computePercentage(valueObj);

  if (percentages.total === 0) {
    return '-';
  }

  return Math.round(percentages[type] * 100);
};

export const FORMAT_VALUE_PERCENTAGES_FAVOURABLE: GetValueFunc = (
  valueObj
): FormatValueResult => {
  return formatValuePercentages(valueObj, 'favourable');
};

export const FORMAT_VALUE_PERCENTAGES_UNFAVORABLE: GetValueFunc = (
  valueObj
): FormatValueResult => {
  return formatValuePercentages(valueObj, 'unfavourable');
};

export const FORMAT_VALUE_PERCENTAGES_NEUTRAL: GetValueFunc = (
  valueObj
): FormatValueResult => {
  return formatValuePercentages(valueObj, 'neutral');
};

export enum AllComparisonMode {
  Report = 'report',
  Organization = 'organization',
}

export interface Props {
  data: Array<object>;
  renderItem: (data: object) => JSX.Element;
  colorScale?: GetColorFunc;
  colorScaleUnfrozen?: GetColorFunc;
  formatValue?: (valueObj: object) => FormatValueResult;
  ratingScale?: RatingScaleItem[];
  unit?: string;
  additionalInputs?: AdditionalInput[];
  allComparisonMode?: AllComparisonMode;
}

export const countResponses = (valueObj): number => {
  // No responses
  if (!valueObj || Object.keys(valueObj ?? {}).length === 0) {
    return 0;
  }
  return Object.keys(valueObj).reduce(
    (acc, curr) => acc + Number(valueObj[curr] ?? 0),
    0
  );
};

const formatValueResultToNumber = (val: FormatValueResult): number => {
  return val === '-' ? 0 : val;
};

const generateCsvData: GenerateCsvData = (
  segmentValues: string[],
  frozenRowData: object[],
  unfrozenRowData: object[],
  formatValue: GetValueFunc,
  allColumnHeaderText?: string
): any[][] => {
  const allHeader = allColumnHeaderText ?? 'All';
  const headers = [
    'Question',
    'Factor',
    allHeader,
    ...segmentValues,
    ...segmentValues.map((x) => `${x} (Delta to ${allHeader})`),
    ...segmentValues.map((x) => `${x} (# of responses)`),
  ];
  const allRows = frozenRowData.map((_frozen, idx) => {
    // @ts-expect-error
    const allValueTxt = formatValue(_frozen?.all?.responses);
    const allValueNum = formatValueResultToNumber(allValueTxt);
    return {
      // @ts-expect-error
      factorRow: _frozen._isHeader === true,
      data: [
        // @ts-expect-error
        stripHtml(_frozen.name),
        // @ts-expect-error
        _frozen._factor ?? 'No factor',
        allValueNum.toString(),
        ...segmentValues.map((sv) =>
          formatValue(unfrozenRowData?.[idx]?.[sv]?.responses).toString()
        ),
        ...segmentValues.map((sv) => {
          const v = formatValue(unfrozenRowData?.[idx]?.[sv]?.responses);
          const vNum = formatValueResultToNumber(v);
          const delta = vNum - allValueNum;
          return delta.toString();
        }),
        ...segmentValues.map((sv) => {
          const v = countResponses(unfrozenRowData?.[idx]?.[sv]?.responses);
          return v?.toString();
        }),
      ],
    };
  });

  return [headers, ...allRows.filter((x) => !x.factorRow).map((x) => x.data)];
};

const LikertHeatmap: FC<Props> = (props) => {
  const { formatMessage } = useIntl();

  const compareAllToOrg =
    props.allComparisonMode === AllComparisonMode.Organization;

  const allColumnHeaderText = compareAllToOrg
    ? formatMessage({
        id: 'app.views.widgets.charts.likert_heatmap.all_header.organization',
        defaultMessage: 'AVG (Organization)',
        description: 'Avg is short of average',
      })
    : formatMessage({
        id: 'app.views.widgets.charts.likert_heatmap.all_header.report',
        defaultMessage: 'AVG (Report)',
        description: 'Avg is short of average',
      });

  const allColumnTooltipText = compareAllToOrg
    ? formatMessage({
        id: 'app.views.widgets.charts.likert_heatmap.all_header_tooltip.organization',
        defaultMessage: 'Average for the entire organization',
      })
    : formatMessage({
        id: 'app.views.widgets.charts.likert_heatmap.all_header_tooltip.report',
        defaultMessage: 'Average for the selected report',
      });

  const getColor = useMemo(() => {
    if (props.colorScale) {
      return props.colorScale;
    }
    return COLOR_SCALE;
  }, [props.colorScale]);

  const getColorUnfrozen = useMemo(() => {
    if (props.colorScaleUnfrozen) {
      return props.colorScaleUnfrozen;
    }
    if (props.colorScale) {
      return props.colorScale;
    }
    return COLOR_SCALE;
  }, [props.colorScale, props.colorScaleUnfrozen]);

  const getValue = useMemo(() => {
    if (props.formatValue) {
      return props.formatValue;
    }
    return FORMAT_VALUE;
  }, [props.formatValue]);

  const getScale = useMemo(() => {
    if (props.ratingScale) {
      return props.ratingScale;
    }
    return LIKERT_SCALE;
  }, [props.ratingScale]);

  const formatPopoverData = useCallback(
    (obj) => {
      if (typeof obj !== 'object') {
        return null;
      }

      const result = computeScalePercentages(getScale, obj);

      return result;
    },
    [getScale]
  );

  const createPopoverChart = useCallback(
    (id, dataObj) => (
      <>
        <hr />
        <div className="justify-content-center">
          <HorizontalBar
            data={getScale.map((v) => ({
              color: v.color,
              value: dataObj[v.value],
            }))}
            // @ts-expect-error
            uniqueId={id}
          />
        </div>
        <br />
        {getScale.map((v, i) => (
          <div key={i} className="d-flex justify-content-between">
            <span>
              <span
                className={'pe-3 me-2 '}
                style={{ backgroundColor: v.color }}
              >
                &nbsp;
              </span>
              <span className="me-5">{v.text}</span>{' '}
            </span>
            <span className="fw-bold">
              {dataObj[v.value] === 0
                ? '-'
                : Math.round(100 * dataObj[v.value]) + '%'}
            </span>
          </div>
        ))}
      </>
    ),
    [getScale]
  );

  const dataWithFactorHeaders = useMemo(() => {
    const groupedByFactor = props.data?.reduce((acc, d) => {
      // @ts-expect-error
      const factor = d?.configs?.factor ?? '';
      if (!acc[factor]) {
        acc[factor] = [];
      }
      acc[factor].push(d);
      return acc;
    }, {});

    const factorKeys = Object.keys(groupedByFactor).sort((a, b) => {
      // Sort by factor, with "" as the last factor
      if (a === '') return 1;
      if (b === '') return -1;
      return a.localeCompare(b);
    });

    const emptyFactorDisplay = formatMessage({
      id: 'app.views.widgets.charts.likert_heatmap.no_factor_header',
      defaultMessage: '(No factor)',
    });
    let dataWithFactorHeaders: object[] = [];
    if (factorKeys.length === 1 && factorKeys[0] === '') {
      dataWithFactorHeaders = props.data;
    } else {
      factorKeys.forEach((factor) => {
        const factorData = groupedByFactor[factor];

        const mergedResponses = {};
        DEFAULT_ENGAGEMENT_SURVEY_FILTERS.forEach((filter) => {
          mergedResponses[filter] = {};

          const filterKeys = factorData.reduce((acc, d) => {
            if (!d.responses[filter]) {
              return acc;
            }
            Object.keys(d.responses[filter]).forEach((key) => acc.add(key));
            return acc;
          }, new Set());

          filterKeys.forEach((key) => {
            mergedResponses[filter][key] = mergeResponses(
              factorData.map(
                (d) =>
                  d.responses[filter][key] || { eligible: 0, responses: {} }
              )
            );
          });
        });

        dataWithFactorHeaders.push({
          all: mergeResponses(factorData.map((d) => d.all)),
          count: 0,
          configs: {
            display_name: factor || emptyFactorDisplay,
          },
          responses: mergedResponses,
          _isHeader: true,
        });
        dataWithFactorHeaders.push(...factorData);
      });
    }
    return dataWithFactorHeaders;
  }, [props.data, formatMessage]);

  const frozenRowData = useCallback(
    (segment) => {
      return dataWithFactorHeaders.map((d) => {
        const data = segment
          ? compareAllToOrg
            ? // @ts-expect-error
              d._all_by_breakdown_org?.[segment]
            : // @ts-expect-error
              d._all_by_breakdown?.[segment]
          : null;
        return {
          all: data,
          count: segment ? sumObjectValues(data?.responses) : null,
          // @ts-expect-error
          name: d.configs?.display_name,
          customPrefix: 'frozen',
          _value: segment ? getValue(data?.responses) : null,
          // @ts-expect-error
          _name: d?.configs?.display_name,
          // @ts-expect-error
          _isHeader: d?._isHeader || false,
          _segment: segment,
          // @ts-expect-error
          _factor: d?.configs?.factor,
        };
      });
    },
    [dataWithFactorHeaders, getValue, compareAllToOrg]
  );

  const unFrozenRowData = useCallback(
    (segment) => {
      return (
        dataWithFactorHeaders?.map((d) => ({
          // @ts-expect-error
          ...(d.responses?.[segment] || {}),
          // @ts-expect-error
          _name: d?.configs?.display_name,
          _segment: segment,
          // @ts-expect-error
          _factor: d?.configs?.factor,
          // @ts-expect-error
          _id: d?.configs?.name,
          // @ts-expect-error
          _isHeader: d?._isHeader || false,
          // @ts-expect-error
          _value: getValue(d.responses?.[segment]?.responses),
        })) ?? []
      );
    },
    [dataWithFactorHeaders, getValue]
  );

  return (
    <Heatmap
      data={props.data}
      colorScale={getColor}
      colorScaleUnfrozen={getColorUnfrozen}
      formatValue={getValue}
      frozenRowData={frozenRowData}
      unFrozenRowData={unFrozenRowData}
      formatPopover={formatPopoverData}
      popoverChart={createPopoverChart}
      renderItem={props.renderItem}
      unit={props.unit}
      additionalInputs={props.additionalInputs}
      allColumnHeaderText={allColumnHeaderText}
      allColumnTooltipText={allColumnTooltipText}
      generateCsvData={generateCsvData}
    />
  );
};

export default React.memo(LikertHeatmap);
